import React, { useState } from 'react';
import { X, Mail, AlertCircle, CheckCircle, HelpCircle } from 'lucide-react';
import { sendBulkEmails } from '../../../services/emailService';

export default function BulkEmailComposerModal({ isOpen, onClose, selectedApplications }) {
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const availablePlaceholders = [
    { key: 'dealershipName', description: 'Dealership name' },
    { key: 'contactPerson', description: 'Contact person name' },
    { key: 'email', description: 'Customer email' },
    { key: 'status', description: 'Application status' },
    { key: 'applicationId', description: 'Application ID' },
    { key: 'date', description: 'Current date' }
  ];

  const handleInsertPlaceholder = (placeholder) => {
    const textarea = document.getElementById('email-content');
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const newContent = 
      htmlContent?.substring(0, start) + 
      `{{${placeholder}}}` + 
      htmlContent?.substring(end);
    setHtmlContent(newContent);
    
    setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + placeholder?.length + 4, start + placeholder?.length + 4);
    }, 0);
  };

  const handleSend = async () => {
    if (!subject?.trim() || !htmlContent?.trim()) {
      alert('Please fill in both subject and email content');
      return;
    }

    setIsSending(true);
    setResult(null);

    try {
      const recipients = selectedApplications?.map(app => ({
        email: app?.customer_email || app?.dealership?.email,
        variables: {
          dealershipName: app?.dealership?.name || 'Valued Partner',
          contactPerson: app?.dealership?.contact_person || 'Customer',
          email: app?.customer_email,
          status: app?.status,
          applicationId: app?.id,
          date: new Date()?.toLocaleDateString()
        }
      }));

      const emailResult = await sendBulkEmails({
        recipients,
        subject,
        htmlContent
      });

      if (emailResult?.success) {
        setResult({
          type: 'success',
          message: `Successfully sent ${emailResult?.data?.successful} emails`,
          details: emailResult?.data
        });
      } else {
        throw new Error(emailResult.error);
      }
    } catch (error) {
      setResult({
        type: 'error',
        message: `Failed to send emails: ${error?.message}`
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setSubject('');
    setHtmlContent('');
    setResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Bulk Email Composer</h2>
              <p className="text-sm text-gray-500">Sending to {selectedApplications?.length || 0} recipients</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Help Toggle */}
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="mb-4 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
          >
            <HelpCircle className="w-4 h-4" />
            {showHelp ? 'Hide' : 'Show'} placeholder help
          </button>

          {/* Placeholder Help */}
          {showHelp && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">Available Placeholders:</h3>
              <div className="grid grid-cols-2 gap-3">
                {availablePlaceholders?.map(placeholder => (
                  <div key={placeholder?.key} className="flex items-start gap-2">
                    <button
                      onClick={() => handleInsertPlaceholder(placeholder?.key)}
                      className="text-xs bg-white px-2 py-1 rounded border border-blue-300 hover:bg-blue-50 transition-colors font-mono"
                    >
                      {`{{${placeholder?.key}}}`}
                    </button>
                    <span className="text-xs text-gray-600">{placeholder?.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subject */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject *
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e?.target?.value)}
              placeholder="Email subject (you can use {{placeholders}})"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Email Content */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Content (HTML) *
            </label>
            <textarea
              id="email-content"
              value={htmlContent}
              onChange={(e) => setHtmlContent(e?.target?.value)}
              placeholder="Write your email content here. Use {{placeholders}} for dynamic content..."
              rows={12}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            />
            <p className="mt-2 text-xs text-gray-500">
              You can use HTML tags for formatting and {`{{placeholders}}`} for dynamic content
            </p>
          </div>

          {/* Result Message */}
          {result && (
            <div className={`mb-4 p-4 rounded-lg flex items-start gap-3 ${
              result?.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              {result?.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  result?.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {result?.message}
                </p>
                {result?.details && (
                  <p className="text-xs text-gray-600 mt-1">
                    Total: {result?.details?.total}, Successful: {result?.details?.successful}, Failed: {result?.details?.failed}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={isSending || !subject?.trim() || !htmlContent?.trim()}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                Send Bulk Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}